import React, { useMemo, useState } from 'react'
import shortid from 'shortid'
import capitalize from 'lodash/capitalize'

import { uploadFileToDsnProject } from 'utils/upload'

import DropZone from '@react/react-spectrum/DropZone'
import IllustratedMessage from '@react/react-spectrum/IllustratedMessage'
import { Image } from '@react/react-spectrum/Image'
import Textfield from '@react/react-spectrum/Textfield'
import { error } from '@react/react-spectrum/Toast'
import Wait from '@react/react-spectrum/Wait'

import { MAX_FILE_SIZE } from '../../config/constants'

import './MediaUpload.scss'

const MediaUpload = ({
  value,
  heading,
  description,
  maxHeight,
  projectId,
  path,
  withTextfield,
  disabled,
  onUpload,
  type = 'image',
}) => {
  const [uploading, setUploading] = useState(false)
  const [cacheSign, setCacheSign] = useState(shortid())

  const onDrop = async (e) => {
    if (!disabled) {
      setUploading(true)
      const file = e.dataTransfer.items[0].getAsFile()
      if (file.size > MAX_FILE_SIZE[type]) {
        error(`File is too large. Please choose an file smaller than ${MAX_FILE_SIZE[type] / 1000000} MB.`)
        setUploading(false)
        return
      }

      const result = await uploadFileToDsnProject(file, projectId, path)
      if (result?.status === 'success' && result?.url) {
        setCacheSign(shortid())
        // add cache-busting timestamp to the URL stored in DB, so the browser loads the latest image version
        const cacheBustedUrl = `${result.url}${result.url.includes('?') ? '&' : '?'}v=${Date.now()}`
        onUpload(cacheBustedUrl)
      }
      setUploading(false)
    }
  }

  const uncachedMedia = useMemo(() => {
    if (!value || !value.trim()) {
      return ''
    }
    try {
      let url = new URL(value)
      url.searchParams.append('cs', cacheSign)
      return url.toString()
    } catch (e) {
      console.debug(`Could not process ${type} URL.`)
      return ''
    }
  }, [value, cacheSign, type])

  return uploading ? (
    <div className='uploading-container'>
      <Wait />
    </div>
  ) : (
    <div className='media-upload'>
      {value ? (
        <DropZone onDrop={onDrop}>
          <div className='media-container'>
            {type === 'image' && (
              <Image src={uncachedMedia} style={{ maxHeight: maxHeight || 'auto' }} alt={`uploaded ${type}`} />
            )}
            {type === 'video' && (
              <video src={uncachedMedia} preload='metadata' style={{ maxHeight: maxHeight || 'auto' }} />
            )}
          </div>
        </DropZone>
      ) : (
        <DropZone onDrop={onDrop} className='empty'>
          <IllustratedMessage heading={heading} description={description || `Drop your ${type} here.`} />
        </DropZone>
      )}
      {withTextfield && (
        <Textfield
          placeholder={`${capitalize(type)} URL`}
          name='logo'
          value={value || ''}
          disabled={disabled}
          onChange={onUpload}
        />
      )}
    </div>
  )
}

export default MediaUpload
